import styled, { keyframes } from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  greyMui,
  purpleMui,
  redMui,
  rydeColors,
} from "@ryde/theme";

const errorBoundaryFloat = keyframes`
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-6px);
  }
`;

const errorBoundaryPulseRing = keyframes`
  0% {
    transform: scale(0.92);
    opacity: 0.45;
  }
  70% {
    transform: scale(1.12);
    opacity: 0;
  }
  100% {
    transform: scale(1.12);
    opacity: 0;
  }
`;

export const ErrorBoundaryFallbackStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "error-boundary-page",
})`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100%;
  padding: 24px 16px;
  box-sizing: border-box;
  background:
    radial-gradient(circle at 15% 10%, ${purpleMui._100} 0%, transparent 45%),
    radial-gradient(circle at 85% 90%, ${redMui._50} 0%, transparent 40%),
    linear-gradient(
      165deg,
      ${rydeColors.white} 0%,
      ${purpleMui._50} 45%,
      ${blueGreyMui._50} 100%
    );

  .error-boundary__card {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    width: min(100%, 520px);
    padding: 56px 32px 32px;
    border-radius: 24px;
    background: ${rydeColors.white};
    box-shadow:
      0 24px 64px rgba(63, 45, 97, 0.16),
      0 4px 12px rgba(0, 0, 0, 0.06);
    border: 1px solid ${greyMui._200};
    overflow: hidden;
    text-align: center;

    &::before {
      content: "";
      position: absolute;
      inset: 0 0 auto 0;
      height: 5px;
      background: linear-gradient(
        90deg,
        ${redMui._500} 0%,
        ${purpleMui._500} 100%
      );
    }
  }

  .error-boundary__icon-wrap {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 96px;
    height: 96px;
    margin-bottom: 4px;
    border-radius: 50%;
    background: radial-gradient(circle, ${redMui._50} 0%, ${redMui._100} 100%);
    box-shadow: 0 8px 24px rgba(244, 67, 54, 0.22);

    &::before {
      content: "";
      position: absolute;
      inset: 0;
      border-radius: 50%;
      background: ${redMui._100};
      animation: ${errorBoundaryPulseRing} 2.4s ease-out infinite;
    }
  }

  .error-boundary__icon {
    position: relative;
    z-index: 1;
    font-size: 52px;
    color: ${redMui._600};
    animation: ${errorBoundaryFloat} 3s ease-in-out infinite;
  }

  .error-boundary__brand {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${purpleMui._700};
  }

  .error-boundary__title {
    margin: 0;
    font-size: 26px;
    font-weight: 700;
    line-height: 1.3;
    color: ${blueGreyMui._800};
  }

  .error-boundary__subtitle {
    margin: 0;
    max-width: 400px;
    font-size: 15px;
    line-height: 1.5;
    color: ${greyMui._600};
  }

  .error-boundary__actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 10px;
    width: 100%;
    margin-top: 4px;

    button {
      min-width: 180px;
      min-height: 44px;
      gap: 10px;
      border-radius: 12px;
      box-shadow: 0 8px 20px rgba(98, 0, 238, 0.28);
      transition:
        transform 0.15s ease,
        box-shadow 0.15s ease;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 12px 26px rgba(98, 0, 238, 0.34);
      }

      .MuiButton-startIcon {
        margin-inline: 0;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .error-boundary__icon-wrap::before,
    .error-boundary__icon {
      animation: none;
    }

    .error-boundary__actions button {
      transition: none;
    }
  }
`;

export const ErrorBoundaryDetailsPanelStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "error-boundary-details-panel",
  },
)`
  width: 100%;
  margin-top: 8px;
  text-align: start;
  border-radius: 12px;
  border: 1px solid ${greyMui._200};
  background: ${greyMui._50};
  overflow: hidden;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(63, 45, 97, 0.08);
  }

  .error-boundary-details__bar {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 10px 12px;
    min-height: 44px;
  }

  .error-boundary-details__preview {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-size: 13px;
    font-family: Roboto, Arial, sans-serif;
    color: ${redMui._700};
    direction: ltr;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .error-boundary-details__actions {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 2px;
  }

  .error-boundary-details__button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: ${blueGreyMui._600};
    cursor: pointer;

    &:hover {
      color: ${blueGreyMui._800};
      background-color: ${greyMui._200};
    }

    &:focus-visible {
      outline: 2px solid ${purpleMui._400};
      outline-offset: 2px;
    }

    .MuiSvgIcon-root {
      font-size: 18px;
    }
  }

  .error-boundary-details__full {
    margin: 0;
    padding: 12px 14px 14px;
    font-size: 12px;
    line-height: 1.45;
    font-family: Roboto, Arial, sans-serif;
    color: ${blueGreyMui._800};
    direction: ltr;
    text-align: left;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 320px;
    overflow: auto;
    border-top: 1px solid ${greyMui._200};
  }
`;
